(() => {
  'use strict';

  const MODULE_KEY = '__totokaBestsellerIndex';
  const ROOT_SELECTOR = '[data-tbi-root]';

  if (window[MODULE_KEY]) {
    window[MODULE_KEY].mount(document);
    return;
  }

  const instances = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  class TotokaBestsellerIndex {
    constructor(root) {
      this.root = root;
      this.reveals = [...root.querySelectorAll('[data-tbi-reveal]')];
      this.media = [...root.querySelectorAll('[data-tbi-product-media]')];
      this.observer = null;
      this.frame = null;
      this.destroyed = false;
      this.onViewportChange = this.onViewportChange.bind(this);

      this.init();
    }

    init() {
      if (reduceMotion.matches) {
        this.showAll();
        this.root.style.setProperty('--tbi-progress', '1');
        return;
      }

      this.root.classList.add('is-motion-ready');

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                this.observer?.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
        );

        this.reveals.forEach((element) => this.observer.observe(element));
      } else {
        this.showAll();
      }

      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      this.onViewportChange();
    }

    showAll() {
      this.reveals.forEach((element) => element.classList.add('is-visible'));
    }

    onViewportChange() {
      if (this.frame || this.destroyed) return;

      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.updateProgress();
      });
    }

    updateProgress() {
      const rect = this.root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      if (rect.bottom < 0 || rect.top > viewportHeight) return;

      const progress = clamp((viewportHeight - rect.top) / (rect.height + viewportHeight));
      this.root.style.setProperty('--tbi-progress', progress.toFixed(4));

      if (window.innerWidth < 750) {
        this.media.forEach((element) => element.style.setProperty('--tbi-parallax', '0px'));
        return;
      }

      this.media.forEach((element, index) => {
        const mediaRect = element.getBoundingClientRect();
        const mediaCenter = mediaRect.top + mediaRect.height / 2;
        const viewportCenter = viewportHeight / 2;
        const normalized = clamp((mediaCenter - viewportCenter) / viewportHeight, -1, 1);
        const amplitude = index === 0 ? 9 : 14;
        element.style.setProperty('--tbi-parallax', `${(-normalized * amplitude).toFixed(2)}px`);
      });
    }

    revealBlock(block) {
      const entry = block?.closest?.('[data-tbi-entry]');
      if (entry) entry.classList.add('is-visible');
    }

    destroy() {
      this.destroyed = true;
      this.observer?.disconnect();
      this.observer = null;

      if (this.frame) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      this.root.classList.remove('is-motion-ready');
      this.root.style.removeProperty('--tbi-progress');
      this.media.forEach((element) => element.style.removeProperty('--tbi-parallax'));
    }
  }

  const mount = (scope = document) => {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : [...scope.querySelectorAll(ROOT_SELECTOR)];

    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new TotokaBestsellerIndex(root));
    });
  };

  const unmount = (scope) => {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : [...scope.querySelectorAll?.(ROOT_SELECTOR) || []];

    roots.forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  const start = () => mount(document);

  window[MODULE_KEY] = { mount, unmount };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    const selectedBlock = blockId
      ? document.querySelector(`${ROOT_SELECTOR} [data-block-id="${CSS.escape(blockId)}"]`)
      : event.target.closest?.('[data-tbi-entry]');
    const root = selectedBlock?.closest?.(ROOT_SELECTOR) || event.target.closest?.(ROOT_SELECTOR);
    const instance = root ? instances.get(root) : null;
    instance?.revealBlock(selectedBlock || event.target);
  });
})();
